import * as React from 'react'
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog'

import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'

const AlertDialogBase = AlertDialogPrimitive.Root

const AlertDialogTriggerBase = AlertDialogPrimitive.Trigger

const AlertDialogPortalBase = AlertDialogPrimitive.Portal

const AlertDialogOverlayBase = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    className={cn(
      'fixed inset-0 z-[99999999] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
    ref={ref}
    onClick={(e) => e.stopPropagation()}
  />
))
AlertDialogOverlayBase.displayName = AlertDialogPrimitive.Overlay.displayName

const AlertDialogContentBase = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>
>(({ className, ...props }, ref) => (
  <AlertDialogPortalBase>
    <AlertDialogOverlayBase />
    <AlertDialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-[50%] top-[50%] z-[9999999999] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
        className
      )}
      {...props}
      onClick={(e) => e.stopPropagation()}
    />
  </AlertDialogPortalBase>
))
AlertDialogContentBase.displayName = AlertDialogPrimitive.Content.displayName

const AlertDialogHeaderBase = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-2 text-center sm:text-left',
      className
    )}
    {...props}
    onClick={(e) => e.stopPropagation()}
  />
)
AlertDialogHeaderBase.displayName = 'AlertDialogHeader'

const AlertDialogFooterBase = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className
    )}
    {...props}
    onClick={(e) => e.stopPropagation()}
  />
)
AlertDialogFooterBase.displayName = 'AlertDialogFooter'

const AlertDialogTitleBase = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold', className)}
    {...props}
    onClick={(e) => e.stopPropagation()}
  />
))
AlertDialogTitleBase.displayName = AlertDialogPrimitive.Title.displayName

const AlertDialogDescriptionBase = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
    onClick={(e) => e.stopPropagation()}
  />
))
AlertDialogDescriptionBase.displayName =
  AlertDialogPrimitive.Description.displayName

const AlertDialogActionBase = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Action
    ref={ref}
    className={cn(buttonVariants(), className)}
    {...props}
  />
))
AlertDialogActionBase.displayName = AlertDialogPrimitive.Action.displayName

const AlertDialogCancelBase = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Cancel
    ref={ref}
    className={cn(
      buttonVariants({ variant: 'outline' }),
      'mt-2 sm:mt-0',
      className
    )}
    {...props}
    onClick={(e) => e.stopPropagation()}
  />
))
AlertDialogCancelBase.displayName = AlertDialogPrimitive.Cancel.displayName

// --- uiv2: conditional Intui swap (build-time resolved by Vite) ---
import {
  IntuiAlertDialog,
  IntuiAlertDialogTrigger,
  IntuiAlertDialogContent,
  IntuiAlertDialogHeader,
  IntuiAlertDialogTitle,
  IntuiAlertDialogDescription,
  IntuiAlertDialogFooter,
  IntuiAlertDialogAction,
  IntuiAlertDialogCancel,
} from '@/components/intueri/IntuiAlertDialog'

const _uiv2 = import.meta.env.VITE_UIV2 === 'true'
const AlertDialog = _uiv2 ? IntuiAlertDialog : AlertDialogBase
const AlertDialogTrigger = _uiv2 ? IntuiAlertDialogTrigger : AlertDialogTriggerBase
const AlertDialogContent = _uiv2 ? (IntuiAlertDialogContent as typeof AlertDialogContentBase) : AlertDialogContentBase
const AlertDialogHeader = _uiv2 ? (IntuiAlertDialogHeader as typeof AlertDialogHeaderBase) : AlertDialogHeaderBase
const AlertDialogFooter = _uiv2 ? (IntuiAlertDialogFooter as typeof AlertDialogFooterBase) : AlertDialogFooterBase
const AlertDialogTitle = _uiv2 ? (IntuiAlertDialogTitle as typeof AlertDialogTitleBase) : AlertDialogTitleBase
const AlertDialogDescription = _uiv2 ? (IntuiAlertDialogDescription as typeof AlertDialogDescriptionBase) : AlertDialogDescriptionBase
const AlertDialogAction = _uiv2 ? (IntuiAlertDialogAction as typeof AlertDialogActionBase) : AlertDialogActionBase
const AlertDialogCancel = _uiv2 ? (IntuiAlertDialogCancel as typeof AlertDialogCancelBase) : AlertDialogCancelBase
const AlertDialogPortal = AlertDialogPortalBase
const AlertDialogOverlay = AlertDialogOverlayBase

export {
  AlertDialog,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
}
